// src/pages/public/PrivacyPage.js — Privacy Policy, "Last updated: November 3, 2026"
// (age gate + under-13 parental consent, guardian card photos). Routed at /privacy
// since 2026-10-26 (it was PrivacyPageV2.js, unrouted, from 2026-09-28); the
// October 3 text it replaced is legal-archive/privacy-2026-10-03.md, and the
// October 26 text this supersedes is legal-archive/privacy-2026-10-26.md.
//
// 2026-11-03 (the owner's calls, no counsel): §6 — a child sends a parent the approval
// link themselves (checkout child_parent_link): the first adult who opens it and signs
// in approves, no child-number check on such a link, and we hold no parent phone
// until then; the texted request stays for older app versions. §4 — the last-name
// rule is gone (claimEligibility) and a wrong family's claim is reported to us. §6 —
// a promo code lowers the first month only, and approving by purchase always charges
// at least $0.50 (polyp-roles' consent floor); the consent record keeps the amount
// and the code. Archived as privacy-2026-11-03.md.
//
// 2026-11-01 (the owner's call): §4 "How a claim is verified" — the child's own
// roster pick, confirmed by the parent typing the child's phone number, also waives
// the last-name rule (checkout's childVouchedFor). Archived as privacy-2026-11-01.md.
//
// 2026-10-28 (wording only, the owner's approval, no counsel): §1 points PlayerWiki
// Manager users at the sections about their data; §6 + §11 say the 30-day consent
// window runs from the child's FIRST request to contact a parent (checkout's
// request_guardian_consent sets `deleteAfter` once; resends never extend it — the
// hold itself starts at the answer) and what a hold does to a pre-gate account's
// @handle and name (kept, unpublished, deleted with the account); §6 says a
// withdrawn/deleted child's Season Pass is cancelled at period end with no refund
// (polyp-roles internal `cancel_pass`, checkout's cancelSeasonPass).
//
// The owner signed this text off WITHOUT counsel on 2026-10-26, after every
// statement was checked against the shipped code and infrastructure (the audit
// notes are in backend/CLEANUP-LEDGER.md "Age gate", paragraph "Privacy V2 audited").
// Routed 2026-10-26, before the age-gate release day (PlayerWiki 1.8.0 released
// and the site flipped 2026-10-28, Stripe live the same day): until then the age-gate
// sentences described the TestFlight build and the released-but-inert backend,
// not the App Store build (which sends no age); FEATURES.ageGate stays off and
// Stripe stays test mode until release day (ledger "Age gate").
//
// Any change to a behaviour this page describes is a change to this page in the
// same task, and any text change is a NEW version: bump "Last updated", archive
// the previous text verbatim in polyp-frontend/legal-archive/ in the same commit
// (its README) — a version string must always resolve to the exact words someone
// saw. Extract with legal-archive/extract/.
//
// Section notes (what each part is grounded in):
// - §2 names what the apps send (push token, app version, time zone — register_device /
//   POST /devices), the optional roster DOB + coach-entered guardian phone, the Locker
//   look and relationship note on a claim, the legacy Children rows, and what the
//   Lambda logs really are (CloudWatch, 90 d; there are no access logs).
// - §4 says what a link preview and a claimed card show (rating, look, photo) and that a
//   verified claim names a player on a numbers-only team; the claim paragraph follows
//   sp_claim (consent-time claim, no child account needed, ID consent = no pass,
//   relationApprovedFor, survives a lapse).
// - §4A: the site has no preview step, drafts go in ~2 days (S3 lifecycle), subjects/
//   live with the photo, controls are the Locker (app) / My children (site).
// - §5: follow-a-pinned-player upload rule, the two cases that rewrite a title
//   (final-whistle reconcile, hide), unclaim un-pins (untag_player), the in-app guardian
//   takedown, reports.
// - §6: resend ≤2 + push, "unknown" funding refused, the EXPIRED_ record's 3 years, the
//   parent-started pass and its one text to the child's number (parent_gift_start /
//   inviteChildIfDue). §8: PITR 35 d, image versions 7 d (polyp-images lifecycle, 2026-10-26),
//   UserActivity deleted on account deletion.
// - §11: leagues, blocks, prefs, Locker picks, the child's added phone, the sticky device
//   marker, the public card under the parent's @handle. §13: the texts that exist
//   (pw-notify game_start/game_end fallback, assignment notices; no game reminders).
// - §14: Apple push, Stripe metadata with the child uid, Rekognition, Google
//   Fonts/reCAPTCHA/Maps. §15: at-rest encryption, public profile pictures + logos.
//   §16: TEAM scope, sponsor click-through.
import React from "react";
import { Link } from "react-router-dom";
import usePlayerWikiTab from "./usePlayerWikiTab";
import "./LegalPage.css";

export default function PrivacyPage() {
  usePlayerWikiTab("Privacy Policy — PlayerWiki");   // title + PlayerWiki tab icon (the console's say Polyp)
  return (
    <div className="legal-bg">
      <div className="legal-card">
        {/* Page chrome, not policy text (legal-archive/extract skips .legal-brand):
            PlayerWiki, back to playerwiki.org, since 2026-10-28 — both sites' Terms /
            Privacy links land here, and "Polyp" read as another company. */}
        <a href="https://playerwiki.org" className="legal-brand">Player<span className="legal-brand-wiki">Wiki</span></a>

        <h1 className="legal-title">Privacy Policy</h1>
                <p className="legal-updated">Last updated: November 3, 2026</p>

        <div className="legal-body">
          <section id="introduction">
            <h2>1. Introduction</h2>
            <p>
              Polyp LLC ("we", "us", "our") operates two products for youth basketball:
            </p>
            <ul>
              <li><strong>PlayerWiki Manager</strong> — league management and live scorekeeping,
                used by leagues, schools, and tournaments. Available as an iOS app and as
                the league console at app.polyp.me.</li>
              <li><strong>PlayerWiki</strong> — a free, public, searchable wiki of youth
                basketball players, teams, and leagues at playerwiki.org, plus the PlayerWiki
                iOS app. PlayerWiki is populated from the games recorded in PlayerWiki Manager.</li>
            </ul>
            <p>
              Some earlier features are no longer part of our products. Data created while
              they were in use remains covered by this policy and is retained or deleted as
              described in Section 8.
            </p>
            <p>
              Together, these products, our websites, mobile apps, and related APIs are the
              "Service". This Privacy Policy explains how we collect, use, disclose, and
              safeguard information when you use the Service. Sections 4, 4A, 5, 6, and 11 are the
              important ones if you are the parent or guardian of a young player — they
              explain exactly what PlayerWiki does and does not publish about minors, how
              highlight video works, how a parent consents for a child under 13, and what a
              child's account can and cannot do.
            </p>
            <p>
              If you use PlayerWiki Manager (the coach, scorekeeper and league app, or the
              league console at app.polyp.me), the parts of this policy that describe your
              data are Section 2 (Account Information and Player Records), Section 4,
              Section 9 ("With your league" and "Leagues and schools"), and Section 13.
            </p>
          </section>

          <section id="what-we-collect">
            <h2>2. Information We Collect</h2>
            <p><strong>Account Information:</strong> When you create an account, we collect
              your name, email address, and phone number, and date of birth where required.
              We also assign your account an internal user ID that identifies it across our
              systems. If you sign in with Apple or Google, we receive your name and email
              address from that provider. If you turn on notifications, we store a device
              push token for each of your devices, the version of our app it runs, and (in
              PlayerWiki) the device's time zone, so game times in notifications read in your
              local time. Neither app collects your location, your contacts, or an
              advertising identifier.</p>
            <p><strong>Birthday:</strong> Everyone who creates a PlayerWiki account is asked
              for their birthday, and accounts created before we asked are asked the next time
              they open the app or sign in. We use it to place the account in an age group (under 13, 13 to 15,
              16 to 17, or 18 and older); to decide whether a parent's consent is needed
              (Section 11) and which features the account can use; and for private features
              that depend on it, such as moving an account into the next age group as its
              owner gets older. On iPhone, the PlayerWiki app may also receive from Apple the
              age range you or your parent chose to share with Apple, and how Apple knows it;
              when the two disagree we use the younger one, and we keep a note of which answer
              decided. Birthdays are never published,
              never shown on a profile, and never shared. If you tell us you are under 13, we
              do not keep the birthday you entered — your parent gives us your birthday when
              they consent (Section 6).</p>
            <p><strong>Player Records:</strong> Each team's coach (or, for league-run teams,
              the league administrator) enters the roster in PlayerWiki Manager — each
              player's first and last name, jersey number, team, and league, and optionally
              their position, their date of birth, and a parent or guardian's phone number —
              and game statistics are recorded as games are played. A guardian phone number
              entered by a coach is kept only to invite that parent to claim the player's card
              (a feature that is currently switched off) and is never published. This
              information is provided by the coach, not by the player, and most players never
              create an account at all. A team's age group or grade band is shown on its
              public page.</p>
            <p><strong>Claim Information:</strong> When a parent or guardian claims a player's
              PlayerWiki Player Card, we record the link between that guardian's account and
              the player record, the username (@handle) chosen for the card, the card's look
              chosen in the Locker (skin, emblem, intro, and stickers), and, if the guardian
              asked us to review their relationship to the player because their last names
              differ, what they told us about it.</p>
            <p><strong>Card Photos:</strong> If you are the parent or guardian who claimed a
              player's card, you can choose to send us a photo of your child for it. We receive
              it only from you, never from your child. Before it reaches us, the PlayerWiki
              app or website removes the photo's location and other embedded details. Section
              4A explains how we use it, who can see it, and how long we keep it.</p>
            <p><strong>Highlight Clips:</strong> Signed-in users at a live game can record and
              upload short highlight video clips through the PlayerWiki app. Clips include the
              audio picked up by the device microphone while recording. Section 5 explains who
              can upload a clip, who can see it, and how long it lasts.</p>
            <p><strong>Parental Consent Records:</strong> When a parent or guardian consents
              for a child under 13, we keep a record of that consent: the parent's account and
              phone number, the version of the notice they read, when the request was made and
              when consent was given, and either the payment details described under Payment
              Information below or, if the parent verified their identity instead, that the
              check succeeded and our identity-verification provider's reference for it.
              Section 6 explains this in full.</p>
            <p><strong>Follows and Saved Clips:</strong> If you are signed in to PlayerWiki, we
              store the players, teams, and leagues you choose to follow, and any highlight
              clips you choose to save to your account. If you have a PlayerWiki @handle,
              your public fan profile shows your @handle, your profile picture, the highlight
              clips you posted, and the teams you follow. The players and leagues you follow
              and the clips you save stay private, as are the accounts you choose to block and
              your notification settings. Accounts of children under 13 have no public
              profile.</p>
            <p><strong>Payment Information:</strong> PlayerWiki Manager and PlayerWiki are free
              to use. The optional Season Pass is paid, and payments are processed by Stripe.
              We do not store card numbers. When a parent consents for a child's account, we
              keep the card brand, last four digits and card type, plus Stripe's payment
              reference, as a record of that consent. Stripe may collect payment details in
              accordance with their privacy policy.</p>
            <p><strong>Legacy Business Data:</strong> Data created under earlier features that
              are no longer offered — including client names, contact information, attendance,
              and payment records, the names and dates of birth of dependents (children) that
              account holders registered and the ratings recorded for them, and messages sent
              through the earlier direct-messaging and announcement features — remains stored
              and covered by this policy.</p>
            <p><strong>Usage Activity:</strong> When you are signed in to the PlayerWiki
              website, we record the pages you view — the page address, the page that referred
              you there, a per-tab session identifier, the time, how long the page stayed open and
              visible, and your account and @handle.
              This is our own record, held on our own servers. It is never published, never
              sold, and never shared with anyone outside Polyp LLC, and we do not collect it
              from the PlayerWiki Manager or PlayerWiki mobile apps or from a child's account.
              Section 8 says how long we keep it.</p>
            <p><strong>Server Logs:</strong> Our application servers keep diagnostic logs of
              the requests they handle: the time, the function called and its outcome, and in
              some cases the IP address and the app or browser version. We keep no separate
              web-server access logs. See Section 8 for how long these are kept.</p>
          </section>

          <section id="how-we-use">
            <h2>3. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul>
              <li>Provide, maintain, and improve the Service</li>
              <li>Publish player profiles (names and statistics), team pages, and league
                pages on PlayerWiki (see Section 4)</li>
              <li>Publish and serve highlight clips (see Section 5)</li>
              <li>Run leagues: schedules, rosters, assignments, and game records</li>
              <li>Place each account in an age group and apply the protections for younger
                users (see Section 11)</li>
              <li>Ask a parent for, and record, their consent to a child's account (see
                Section 6)</li>
              <li>Process transactions and send related notifications</li>
              <li>Send transactional messages (staff invitations, assignment notices, game
                alerts for the teams and players you follow, payment confirmations)</li>
              <li>Respond to customer support requests</li>
              <li>Understand how the Service is used to improve it — in aggregate, and, for
                signed-in visitors to the PlayerWiki website, as the per-account page-view
                record described in Section 2</li>
              <li>Detect, prevent, and address technical issues or fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p>
              We do not sell your personal information, and we do not "share" it for
              cross-context behavioral advertising as those terms are defined by California
              law. We have no actual knowledge of selling or sharing the personal information
              of anyone under 16. We do not use your personal information to target
              advertising, and we do not share it with data brokers. Section 16 explains how
              sponsor messages are selected.
            </p>
          </section>

          <section id="youth-players">
            <h2>4. Youth Players and Public Profiles</h2>
            <p>
              PlayerWiki is a public website, and anyone can read it without an account. Most
              of the players it covers are minors, so this section sets out exactly what it
              shows about a player and what it never shows.
            </p>
            <p><strong>Where player names come from.</strong> A team's coach adds each player
              to the team's roster in PlayerWiki Manager, including the player's first and
              last name. By adding a player, the coach confirms under our Terms of Service that
              they are authorized to share that player's information and to have it shown
              publicly on PlayerWiki. Leagues and coaches are responsible for obtaining any parental
              consent their own program or local law requires (Section 11).</p>
            <p><strong>What is public for every rostered player.</strong> As soon as a coach
              adds a player, and without anyone claiming the player's card, PlayerWiki shows
              the player's full name, jersey number, position, team, and league, together with
              their season averages, game-by-game statistics, and the box scores of the games
              they played in. The player has a PlayerWiki page and appears in PlayerWiki
              search results.</p>
            <p><strong>Search engines and shared links.</strong> PlayerWiki's player, team,
              league, search, and clip pages ask search engines not to index them. Anyone can
              still open those pages, and anyone can share a link to them; a shared link can
              display a preview card with the player's name, jersey number, position, team, and
              statistics and, for a claimed card, its @handle, the card's look, and any card
              photo, wherever it is posted. Messaging apps may keep such a preview after the
              card has changed.</p>
            <p><strong>What a claimed card adds.</strong> When a parent or guardian claims a
              player's card, the chosen @handle, the card's look (the skin, emblem, intro, and
              stickers chosen in the Locker), and any card photo the parent or guardian sent
              (Section 4A) are also shown, and the card may show a rating derived from the
              player's statistics. Claiming does not change whether the player's name and
              statistics are public, with one exception: a claimed card always shows the
              player's name, even on a team that asked us to show jersey numbers only.</p>
            <p><strong>These fields are never published for any player:</strong> date of birth,
              email address, phone number, home address, precise location, or any guardian's
              contact information. We publish a photograph of a player only on their claimed
              Player Card, and only when the parent or guardian who claimed it sends us one
              (Section 4A); we never publish player photographs on rosters. Highlight video
              clips may appear in public game and team feeds as described in Section 5. A
              parent or guardian can have any clip or card photo of their child
              removed.</p>
            <p><strong>Hiding a player's name.</strong> A parent or guardian who does not want
              their child's name shown can email{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a>, and we will work with the
              player's team to hide or remove the name promptly. A team or league can also ask
              us to show only jersey numbers for its whole roster. See Section 7.</p>
            <p><strong>Teams and leagues</strong> are organizations, not individuals, and their
              pages, rosters, schedules, and results are shown normally.</p>
            <p><strong>How a claim is verified, and its limits.</strong> A parent or guardian
              claims a player's card for their child when they consent for that child
              (Section 6): either when they buy the child's Season Pass, or, if they consented
              by verifying their identity instead, for the card they picked at that time. The
              child need not have a PlayerWiki account yet; the card is linked to the parent's
              account, and to the child's account once the child joins. The parent must be 18
              or older. We start from the roster spot the child picked themselves (Section 6);
              we do not check that the parent's last name matches the player's. Each player's
              card can be claimed only once, and the claim stays until the parent removes it,
              even if the Season Pass lapses. This is not proof of guardianship. A parent who
              finds their child's card already claimed can tell us from the approval screen, and
              we check with the league before we move it. If you believe a Player Card has been
              claimed by someone who is not the player's parent or guardian, email{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a> and we will remove it — see
              Section 7.
            </p>
            <p><strong>Where the statistics come from.</strong> Every number on PlayerWiki was
              recorded by the league or coach scoring the game, in PlayerWiki Manager, as the game was
              played. We do not estimate, scrape, or purchase player statistics.</p>
          </section>

          <section id="card-photos">
            <h2>4A. Card Photos</h2>
            <p>
              A claimed Player Card can show a photo of the player. Only the parent or guardian
              who claimed the card can send one — never the child, and never anyone else — and
              sending one is entirely optional.
            </p>
            <p>
              <strong>What happens to a photo you send.</strong> When you choose a photo, it is
              uploaded to our servers, where our own system cuts your child out of the background
              and places them on the card. In the PlayerWiki app you see the finished card
              before you send it; on playerwiki.org the photo goes to review as soon as it is
              uploaded. A photo you upload but do not send is deleted within two days. A
              person on our staff reviews
              every photo before anything is shown, and may adjust the cut-out on our own
              systems. Once the photo is on the card, you can move, resize and turn it. We do
              not send card photos to any outside editing or AI service. We may decline a photo;
              if we do, we tell you why, and nothing is shown.
            </p>
            <p>
              <strong>Who can see it.</strong> Player Cards are public, so a card photo can be
              seen by anyone who views the card, in the PlayerWiki app and on playerwiki.org,
              and it appears in card images that anyone can save and share. The finished card
              image is served from a public web address so the card can be displayed. The
              photo you originally sent is kept in private storage and is never published.
              Because the card image is public, a copy of it can remain for a time in a
              viewer's browser cache or in a link preview that was already posted after you
              take the photo off the card.
            </p>
            <p>
              <strong>What we don't do.</strong> We do not use card photos for facial
              recognition or to derive biometric identifiers, to select or target ads, or to
              train artificial intelligence models, and we do not sell them.
            </p>
            <p>
              <strong>Your control.</strong> You can withdraw a photo that is still being
              reviewed, or take a published photo off the card, at any time from the card's
              Locker in the PlayerWiki app, from the "My children" section on playerwiki.org,
              or by emailing{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a>. How long we keep each copy is
              set out in Section 8.
            </p>
          </section>

          <section id="clips">
            <h2>5. Highlight Clips</h2>
            <p>
              A signed-in user watching a live game can record a short video clip through the
              PlayerWiki app and attach it to a player. Clips carry the audio the device
              microphone picks up while recording.
            </p>
            <p><strong>Who can upload one.</strong> Clips can only be uploaded for a game that
              is live or was played within about the last seven days, by someone who follows
              the team that played or a player the clip is pinned to. A clip can be pinned to
              a player's own profile only while that player's card is claimed and verified;
              every other clip belongs to the game and team feeds. Every clip shows the
              @handle of the fan who posted it, linking to their public fan profile.</p>
            <p><strong>Clip titles and captions name the play.</strong> When a clip is cut,
              the PlayerWiki app writes its title and caption automatically from the game's
              live statistics at the moment of the play — for example, the type of play, the
              jersey number and name of the player who made it (and of any player who
              assisted), both teams, the game date, the game clock, and the score. A player
              can be named this way whether or not their card has been claimed. The uploader
              can edit the title before posting. The title and caption are saved with the
              clip. We rewrite them in two cases: when the scorekeeper corrects the play after
              the game (the clip is re-titled, or removed if the play was deleted), and when a
              player's name is hidden (Section 7), in which case the player's full name is
              removed from the clip's title and caption and the clip is un-pinned from their
              page. Unclaiming a card also un-pins the clips tagged while it was claimed. To
              have a name taken out of a clip's title or caption, email{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a> and we will edit the clip or
              remove it.</p>
            <p><strong>Who can see one.</strong> Clips appear in PlayerWiki's public game and
              team feeds, so anyone viewing PlayerWiki can watch them, along with their titles
              and captions. We
              store clip video in private storage — clips are never published at a permanent
              public web address. Viewers receive short-lived links that expire within about
              an hour.</p>
            <p><strong>How long they last.</strong> Clips stay up until the person who
              uploaded them deletes them; they are not removed on a timer. Deleting a clip
              removes it from every feed on PlayerWiki. A signed-in fan may save a clip to
              their own account, which keeps their copy if the original is later taken
              down.</p>
            <p><strong>Removing a clip.</strong> A parent or guardian can have any clip of
              their child removed by emailing{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a>, and a parent or guardian who
              has claimed their child's card can remove any clip linked to their child directly
              in the PlayerWiki app. When we remove a clip at a guardian's request, we delete it
              from the public feeds, from our storage, and from the private copies any fans had
              saved.</p>
            <p><strong>Reporting a clip.</strong> Anyone can report a clip from the PlayerWiki
              app, and a signed-in fan can block an uploader. We keep each report — the reason,
              any note the reporter wrote, and the reporting account, if any — for our own
              review. Reports are never shown to other users.</p>
            <p><strong>Other people in a clip.</strong> Clips are filmed by people at the game
              and may incidentally capture other players, spectators, and crowd audio.
              Uploaders are responsible for complying with their venue's rules and with local
              recording laws. If you or your child appears in a clip and you want it removed —
              whether or not your child is the featured player — email{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a> and we will take it down. Clips
              must not be used to harass, embarrass, or identify a child beyond their play on
              the court.</p>
          </section>

          <section id="verification">
            <h2>6. Parental Consent for Children Under 13</h2>
            <p>
              A child under 13 can use a PlayerWiki account only after a parent or legal
              guardian gives verifiable consent. This is how it works.
            </p>
            <p><strong>The request.</strong> When someone who tells us they are under 13 signs
              up, we ask them to find their own spot on their team's roster by typing their
              first and last name, and then to send a parent or guardian a link from their own
              phone, for example by text message. Until the parent consents, the only
              information we keep is what we need to ask: the child's sign-in details (the
              phone number, or the name and email address Apple or Google shares at sign-in),
              the fact that the account belongs to someone under 13 (not their birthday), and
              the roster spot the child picked (we check the name they typed against the roster
              and do not keep it). We do not receive the parent's phone number until the parent
              opens the link and signs in. The first adult who opens the link and signs in is
              the one who can approve; anyone else who opens it sees only that someone is
              already approving it, and nothing about the child is shown before sign-in. Until
              a parent consents, the child can make a new link, which stops the old one from
              working.</p>
            <p><strong>Older versions of the PlayerWiki app.</strong> Older versions ask the
              child for a parent's phone number instead, and we keep that number with the
              request. We text the parent one message with a link to review and approve the
              account; the child can ask us to re-send it, at most twice. If the parent's phone
              number belongs to a PlayerWiki account, that account also receives an app
              notification.</p>
            <p><strong>What the parent does.</strong> The parent signs in with their phone
              number and confirms they are 18 or older. If we texted the parent, before we show
              them anything about the request, the parent types the child's phone number; we
              compare it with a scrambled copy of the number the child signed up with, never
              show the child's number, and lock the request after five wrong tries until the
              child sends it again. Next, the parent reads our notice to parents, which
              explains what the child's account collects and what the parent can control. The
              parent then enters the child's birthday and gives consent in one of two ways:</p>
            <ul>
              <li><strong>By buying the Season Pass</strong> with a credit or debit card through
                Stripe's secure checkout. Prepaid cards, and cards whose type Stripe cannot
                determine, are not accepted: a purchase made with one is refunded and does not
                count as consent. A promo code can lower the price of the first month, but
                approving this way always charges the card at least $0.50; a checkout that
                charges nothing does not count as consent. The purchase is always made
                through Stripe on our website or in a secure browser window in the PlayerWiki
                app.</li>
              <li><strong>By verifying their identity, for free.</strong> Stripe, acting as our
                service provider, checks a photo of the parent's government-issued ID (driver's
                license, passport, or ID card) and a live selfie that it matches to the photo on
                the ID, to confirm that the parent is an adult. The check takes place on
                Stripe's secure page, on our website or in a secure browser window in the
                PlayerWiki app; we never receive the ID or the selfie. Our servers read the
                birthday on the ID once, only to confirm the parent is 18 or older, and do not
                store it. As soon as the check is finished, whether it succeeds or not, we have
                Stripe delete the ID, the selfie, and the details read from them. Approving this
                way does not include the Season Pass.</li>
            </ul>
            <p><strong>The consent record.</strong> When consent is given, we keep the version
              of the notice the parent read, the dates of the request and the consent, and how
              the parent consented. For a Season Pass purchase, that is the card brand, last
              four digits and card type (credit or debit), the amount the first payment charged and
              any promo code used, and Stripe's payment references; we
              never receive or store the full card number, its expiry date, the name on the
              card, or a billing address — Stripe collects those and they never reach our
              servers. For an identity check, it is only that the check succeeded and Stripe's
              reference for it — no ID details, no birthday, and no images. The record identifies the parent, not the
              child: it does not contain the child's name, birthday, or account. Section 8 says
              how long we keep it.</p>
            <p><strong>If no one consents.</strong> The parent has 30 days from the child's
              first request to contact them. The account is on hold from the moment the child
              answers, and neither a new link nor asking us to re-send a text extends the deadline. If
              they do not consent in that time, we delete the child's account and
              everything collected from the child, together with the parent's phone number if
              we have it;
              only a record that the request expired remains, which we keep for 3 years and
              which identifies neither of them.</p>
            <p><strong>A Season Pass a parent starts.</strong> A parent or guardian can also
              start a Season Pass for their child before the child has asked for an account.
              The parent picks the child's player card, gives us the child's phone number, and
              consents by buying the pass as above. After the purchase we send that phone
              number one text message with a link to join. A brand-new PlayerWiki account that
              signs in with that number becomes the child's account under the parent's
              consent; an existing account that is waiting for a parent's consent is asked
              first; any other account is left alone. Until the child joins, we keep the
              child's phone number and the card the parent picked with the parent's request,
              and the parent can cancel it at any time from "My children". We never tell the
              parent whether a number already has an account.</p>
            <p><strong>If the Season Pass ends.</strong> Consent does not end with the Season
              Pass: if the subscription is later cancelled or lapses, the child's account is
              not deleted. To withdraw consent or delete the account, see Section 11.</p>
            <p><strong>If consent ends.</strong> If you withdraw consent or the child's account
              is deleted — or, for a Season Pass you started, if the child does not join
              before the request expires — the Season Pass is cancelled at the end of the
              period already paid for. There is no refund for the remainder of that period,
              and nothing more is charged. The consent record is kept as described in
              Section 8.</p>
            <p>
              We do not use facial recognition, and we do not derive biometric identifiers
              from clips, card photos, or any other image we hold. The one face comparison in
              the Service is the optional identity check above: Stripe compares the parent's
              selfie with the photo on their ID, for that check only, and deletes both as soon
              as it is finished; we never receive the images or any facial data.
            </p>
          </section>

          <section id="removal">
            <h2>7. Removing a Player's Public Record</h2>
            <p>
              A parent or guardian can ask us at any time to hide their child's name or take
              their child's public page down, and nobody has to justify the request to us.
            </p>
            <ul>
              <li><strong>By email:</strong> write to{" "}
                <a href="mailto:help@polyp.me">help@polyp.me</a> from any address, and tell us
                the player's name and team (or @handle). You do not need an account. This is
                also the route to use if a card was claimed by someone who is not the player's
                guardian — we will remove that claim.</li>
              <li><strong>What we do:</strong> we work with the player's team to hide or remove
                the player's name from PlayerWiki promptly, or take the player's public page
                down, and tell you when it is done.</li>
            </ul>
            <p>
              Hiding a name or taking a page down does not by itself erase the team's or
              league's own scorebook, which they control. If you want the underlying player record deleted as
              well, say so in your email and we will delete it on the schedule in Section 8
              and tell you when it is done. Tell us if you also want your child's highlight
              clips removed and we will delete those, including copies fans have saved
              (Section 5). A card photo can be taken off the card the same way, or by the
              parent or guardian who sent it at any time (Section 4A).
            </p>
          </section>

          <section id="retention">
            <h2>8. Data Retention</h2>
            <p>
              We retain information for as long as your account is active or as needed to
              provide the Service. When you delete your account, or when we honor a deletion
              request under Section 7 or Section 10, we will delete or anonymize the relevant
              personal information within 30 days, except where we are required to retain it
              for legal or legitimate business purposes.
            </p>
            <p>Some retention periods are fixed:</p>
            <ul>
              <li>Highlight clips stay up until their uploader deletes them.</li>
              <li>A clip a fan saved is kept until that fan deletes it, until they close their
                account, or until we remove the clip at a guardian's request.</li>
              <li>Verification ID photographs collected under an earlier verification process
                are deleted as soon as their review is decided.</li>
              <li>A card photo you send (Section 4A) is deleted as soon as you withdraw it or we
                decline it; otherwise the photo as you sent it is deleted automatically 180
                days after you send it. The cut-out of your child made for the card is kept
                for as long as the photo is on the card, so you can move and resize it, and is
                deleted with the photo; other working copies are deleted within 30 days. Our
                record that a photo was sent, reviewed, declined or removed — without the
                image — is kept with the card's claim.</li>
              <li>A published card photo stays on the card until you take it off, the card is
                unclaimed, your account or your child's account is deleted, or you withdraw
                consent for your child's account; a replaced photo is deleted when the new one
                is published.</li>
              <li>The record of a parent's consent (Section 6) is kept for as long as the
                child's account exists and for 3 years after it is deleted, as proof that
                consent was given. It contains nothing about the child.</li>
              <li>If a parent does not consent within 30 days, the child's account and
                everything collected from the child are deleted (Section 6). If a parent
                withdraws consent, we delete the child's account and its data
                promptly.</li>
              <li>Diagnostic server logs (Section 2) are kept for 90 days and then deleted
                automatically.</li>
              <li>Website usage-activity records (Section 2) are deleted automatically 180 days
                after they are recorded, and are deleted at once when the account is
                deleted.</li>
              <li>Reports of clips (Section 5) are kept for our review; once the reporting
                account is deleted, the report no longer names it.</li>
              <li>Our database keeps point-in-time recovery backups for 35 days. Information
                you delete can remain in those backups for up to 35 days and is then gone; we
                do not restore backups to recover deleted personal information. Deleted profile
                pictures and team logos can remain in storage backups for up to 7 days.</li>
            </ul>
            <p>
              After a deletion, entries relating to the deleted information may remain in
              those diagnostic logs and backups until they age out on the schedules above.
            </p>
          </section>

          <section id="sharing">
            <h2>9. How We Share Your Information</h2>
            <p>We do not sell your personal information. Information is shared as follows:</p>
            <ul>
              <li><strong>Publicly, on PlayerWiki:</strong> players' names and statistics,
                claimed Player Cards (including any card photo a parent or guardian sent),
                highlight clips, fan profiles (a fan's @handle, profile picture, posted clips,
                and followed teams), and team and league pages are public by design and may be
                shared as links and images. Sections 2, 4, 4A, and 5 define exactly which fields
                and media this covers.</li>
              <li><strong>With your league:</strong> the league, its administrators, and its
                coaches can see the roster and game information they maintain about their
                own players.</li>
              <li><strong>Leagues and schools:</strong> organizations that record games in
                PlayerWiki Manager control their own scorebook and roster data. For their programs
                we act as their service provider and process that data on their
                instructions.</li>
              <li><strong>Service Providers:</strong> third-party vendors who help us operate
                the Service (see Section 14).</li>
              <li><strong>Legal Requirements:</strong> when required by law, regulation, or
                legal process.</li>
              <li><strong>Business Transfers:</strong> in connection with a merger,
                acquisition, or sale of assets.</li>
            </ul>
          </section>

          <section id="your-rights">
            <h2>10. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you or your child</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Request a portable copy of your data</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p>
              To exercise these rights, contact us at{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a>. If you are a parent or
              guardian, you may exercise these rights on behalf of your child. Section 7 is
              the fastest route for taking a player's public page down.
            </p>
            <p>
              We respond to rights requests within 45 days. We will verify your identity
              before acting — usually by confirming control of the email address or phone
              number on the account. We will not discriminate against you for exercising any
              right. If we decline a request, we will tell you why, and you may appeal by
              replying to our decision; we answer appeals within 45 days.
            </p>
          </section>

          <section id="childrens-privacy">
            <h2>11. Children's Privacy</h2>
            <p>
              <strong>Who can hold an account.</strong> You must be at least 13 years old to
              create a Polyp account on your own. Coaches, officials, league staff, and anyone
              using PlayerWiki Manager must be 18 or older. Users aged 13 to 17 need a parent
              or guardian's permission, as our Terms of Service explain. A child under 13 can
              have a PlayerWiki account only with verifiable parental consent, which a parent
              gives by buying the Season Pass with a credit or debit card or by verifying their
              identity with a photo ID (Section 6).
            </p>
            <p>
              <strong>How we ask.</strong> A new PlayerWiki account is asked for its owner's
              birthday right after it signs in, before we ask for anything else — no @handle,
              name, or additional phone number comes first. Signing in has to come first because
              the answer belongs to an account: at that moment we hold only the sign-in details
              (the phone number, or the name and email address Apple or Google shares). Existing
              accounts are asked the next time they open the app or sign in. The PlayerWiki app
              also remembers on the device that someone answered under 13 there, so that a
              brand-new account created on that device is treated as a child's; this never
              applies to an existing account. If the answer is under 13, we do not keep
              the birthday; we record only that the account belongs to someone under 13. The
              account is then put on hold until a parent consents: nothing about it is public,
              its notifications stop, and any clips it posted are hidden. If no parent consents
              within 30 days of the child's first request to contact one, we delete the account and everything collected
              from the child, including the sign-in itself and the parent's phone number. We
              keep only a record that a request expired, which identifies neither the child nor
              the parent.
            </p>
            <p>
              <strong>What a child's account collects.</strong> The phone number the child
              signs in with, or the name and email address Apple or Google shares when they
              sign in (and, if the child later adds their own phone number to sign in with,
              that number); the birthday their parent gives us; the players, teams, and
              leagues the child follows; the accounts the child blocks and their notification
              settings; the card designs the child picks in the Locker for the player card
              their parent claimed; and the device tokens we use to send notifications to the
              child's own devices.
            </p>
            <p>
              <strong>What a child's account cannot do.</strong> A child's account has no
              public @handle and no public profile of its own, cannot upload, like, or save
              highlight clips, cannot add a profile photo, is not tracked on the website, and
              does not receive text messages — the one exception is the single text sent to a
              phone number a parent gives us when they start a Season Pass (Section 6). The
              player card a parent claims for the child is public, under the @handle the
              parent chose, and shows the name and statistics the roster already shows. We do
              not ask a child for more information than the account needs.
            </p>
            <p>
              <strong>Card photos.</strong> A parent or guardian who has claimed their child's
              player card can choose to send a photo of their child for it (Section 4A). The
              photo comes from the parent, never from the child, and it is public on the card.
              The parent can take it off the card at any time, and withdrawing consent deletes
              it.
            </p>
            <p>
              <strong>Parents' rights.</strong> A parent or guardian can review the
              information we hold about their child's account, have it deleted, or withdraw
              consent at any time — from the "My children" section of the parent page linked
              in our text message, from the PlayerWiki app, or by emailing{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a>. Withdrawing consent deletes
              the child's account and its data. We keep only the consent record described in
              Section 6, which contains nothing about the child.
            </p>
            <p>
              <strong>If we learn a child is under 13 without consent.</strong> If we learn
              that we have collected personal information from a child under 13 without a
              parent's consent, we put the account on hold as described above: nothing about
              it is public, its notifications stop, any clips it posted are hidden, and we
              collect nothing more from it. An account that already had an @handle and a name
              before it told us its age keeps them during the hold, unpublished, and they are
              deleted with the account. If no parent consents within 30 days, we delete the
              account and everything collected from the child.
            </p>
            <p>
              We do, however, hold information <em>about</em> minors, because youth sports
              records are records about minors. That information reaches us in one way: a
              player's coach (or league administrator) enters their own team's roster and game
              results. PlayerWiki then shows each player's name and statistics publicly, as
              described in Section 4 — no claim is needed for that.
            </p>
            <p>
              A parent or guardian can review what we hold about their child, correct it, take
              its public page down, or have it deleted — at any time, without giving a reason.
              Sections 7 and 10 explain how. Leagues and coaches that manage minors' records
              are responsible for obtaining any parental consent their own program or local
              law requires before entering a child's information.
            </p>
            <p>
              If you believe a child's information is on the Service and should not be, email{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a> and we will take the public
              page down promptly and delete the underlying record on request.
            </p>
          </section>

          <section id="international">
            <h2>12. International Users</h2>
            <p>
              Polyp LLC, a United States company, is the controller of personal information
              processed through the Service, and all data is stored on servers in the United
              States (sign-in credentials are held by Google's Firebase service); public pages
              and images are served through a worldwide content delivery network. If you use
              the Service from outside the U.S., your information is transferred to and
              processed in the U.S.
            </p>
            <p>
              If you are in the EEA, the UK, or Switzerland: we process account data to
              perform our contract with you; we process player records and publish players'
              names and statistics on the basis of the authorization of the team or league
              that entered them and our legitimate interest in publishing youth sports
              results; we process claimed Player Cards and highlight clips on the basis of the
              consent given when a card is claimed or a clip is posted, and card photos on the
              consent of the parent or guardian who sends them; you may object to the
              publication of a player's record at any time (Sections 7 and 10); and you may
              lodge a complaint with your local supervisory authority.
            </p>
          </section>

          <section id="sms">
            <h2>13. SMS &amp; Communications</h2>
            <p>
              PlayerWiki Manager sends SMS messages for staff invitations (inviting a coach or
              official to a league) and for officiating-assignment notices. PlayerWiki uses
              your phone number to verify your account and, if you follow a team or player and
              have no PlayerWiki app installed, to text you when that team's game starts and
              when its final score is in; each text includes a link and "Reply STOP to opt
              out". We do not send marketing texts. By providing your phone number you consent
              to receive these messages. You can opt out at any time by replying STOP, in the
              app's notification settings, or by contacting us. Message and data rates may
              apply. When a child under 13 asks for an account, we send their parent one text
              message with a link to review and approve it (re-sent at most twice at the
              child's request). When a parent starts a Season Pass for a child, we send the
              phone number the parent gives us one text with a link to join (Section 6).
              Children's accounts otherwise never receive text messages.
            </p>
          </section>

          <section id="third-parties">
            <h2>14. Third-Party Services</h2>
            <p>
              The Service is built on Firebase (Google) for sign-in, Amazon Web Services for
              hosting, storage, and SMS delivery, and Stripe for payment processing, including
              the Season Pass purchase a parent makes to consent for a child, and for the
              optional identity check a parent can use to consent instead (Section 6). Stripe's
              records of a Season Pass name the internal account ID of the child it is for.
              Apple delivers our app notifications and lock-screen Live Activities, and, if
              you choose, Sign in with Apple and the age range described in Section 2. If you
              sign in with Google, Google processes that sign-in under its own policy. An
              Amazon Web Services image-screening service checks each profile picture for
              inappropriate content and text before we store it; it does not identify faces.
              On the PlayerWiki website, Google's sign-in and reCAPTCHA scripts and Google
              Fonts load from Google's servers, and on the console sign-up form Google Maps
              suggests addresses; each of these receives your IP address when the page loads.
              These services have their own privacy policies, and we encourage you to review
              them. We are not responsible for the privacy practices of third-party services.
            </p>
          </section>

          <section id="security">
            <h2>15. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information,
              including encryption in transit (TLS/SSL) and at rest, secure cloud
              infrastructure (AWS), access controls, and private storage with short-lived
              access links for clip and verification media and for card photos as they were
              sent; anyone holding such a link can use it until it expires. Published card
              photos, profile pictures, and team logos are served from public web addresses by
              design (Sections 4A and 2). However, no method of
              electronic transmission or storage is 100% secure, and we cannot guarantee
              absolute security.
            </p>
            <p>
              If a breach of our systems affects your personal information, we will notify you
              and the appropriate authorities as required by applicable law, without undue
              delay.
            </p>
          </section>

          <section id="cookies-and-ads">
            <h2>16. Cookies, Tracking &amp; Sponsor Ads</h2>
            <p>
              We use essential cookies and local storage to maintain your session and
              preferences. We do not use third-party advertising cookies or tracking pixels,
              and we do not track you across other companies' apps and websites. We use no
              third-party analytics services. Google's sign-in and reCAPTCHA scripts on the
              PlayerWiki website may set their own cookies (Section 14). Our servers keep
              diagnostic logs as described in Sections 2 and 8. We also keep our own record of
              which pages
              signed-in visitors view on the PlayerWiki website — described in Section 2 and
              retained for the period in Section 8 — which we use to understand how the site
              is used, never to target advertising.
            </p>
            <p>
              <strong>Sponsor ads.</strong> Some highlight clips are accompanied by a sponsor
              message. Sponsors are chosen by the context of the content — the team, league,
              region, or age bracket of the game — never by who is watching. We build no
              advertising profiles, we do not use your personal information to select ads, and
              we count ad views and clicks only in aggregate against the sponsor's campaign,
              not against you or your child. If you tap a sponsor's message you leave for the
              sponsor's own site, which sees your visit as it would any other.
            </p>
          </section>

          <section id="changes">
            <h2>17. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of
              material changes by posting the updated policy on the Service with a new "Last
              updated" date. Your continued use after changes constitutes acceptance of the
              revised policy.
            </p>
            <p>
              If a change materially affects what we publish or collect about players, we will
              also notify claiming parents and guardians directly, at the phone number or email
              address on their claim, before the change takes effect for their child.
            </p>
          </section>

          <section id="contact">
            <h2>18. Contact</h2>
            <p>
              If you have questions about this Privacy Policy, or want a player's public page
              or a highlight clip removed, contact us at{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a> or by phone at{" "}
              <a href="tel:3058786594">305-878-6594</a>. Legal terms, including governing law,
              are in our <Link to="/terms">Terms of Service</Link>.
            </p>
            {/* COPPA's notice rules require the operator's name, physical address,
                phone and email; the address is the owner's (2026-10-23). */}
            <p>
              Polyp LLC, 7901 4th St N, Ste 300, St. Petersburg, FL 33702.
            </p>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/terms">Terms of Service</Link>
          <span className="legal-footer-sep">&middot;</span>
          <a href="https://playerwiki.org">Back to PlayerWiki</a>
        </div>
      </div>
    </div>
  );
}
